import React, {
  createContext, FC, useContext, useReducer,
} from 'react';
import { AssetUpdateMode } from '../config/constants';
import AssetUpdateReducer from '../reducers/assetUpdate';
import { ActionFromPayloadMap, AssetUpdate, PlanResponseLog } from '../config/types';

export type AssetUpdatePlanResponse = {
  create: number,
  archive: number,
  unchanged: number
  totalCount: number,
  dbAssetTypeCount: number,
  updates: { byLabel: AssetUpdate[], byTwinID: AssetUpdate[] },
  logging: {
    errors: PlanResponseLog[],
    warnings: PlanResponseLog[],
    info: PlanResponseLog[],
    errorCount: number,
    warningCount: number,
    infoCount: number,
  }
  updateByLabel: number,
  updateByTwinID: number,
};

export type AssetUpdateStateType = {
  isPlanningAsset: boolean
  isApplyingAsset: boolean
  plannedUpdatedAsset: AssetUpdatePlanResponse | null
  fileName: string | null
  importTime: Date | null;
  fileContents: string | null
  assetUpdateMode: AssetUpdateMode
  showChangesOnly: boolean
  error: Error | null
};

const initialState: AssetUpdateStateType = {
  isPlanningAsset: false,
  isApplyingAsset: false,
  plannedUpdatedAsset: null,
  fileName: null,
  importTime: null,
  fileContents: null,
  assetUpdateMode: 'upsert',
  showChangesOnly: false,
  error: null,
};

export type AssetUpdateActionPayloadMap = {
  SET_ASSET_UPDATE_FILE: { name: string; contents: string };
  SET_IS_PLANNING_ASSET_UPDATE: boolean;
  SET_ASSET_UPDATE_PLAN: AssetUpdateStateType['plannedUpdatedAsset'];
  SET_IS_APPLYING_ASSET_UPDATE: boolean;
  SET_ASSET_UPDATE_ERROR: AssetUpdateStateType['error'];
  RESET_ASSET_UPDATE: undefined;
  SET_ASSET_UPDATE_MODE: AssetUpdateMode;
  SET_ASSET_UPDATE_SHOW_CHANGES_ONLY: boolean;
};

export type AssetUpdateAction = ActionFromPayloadMap<AssetUpdateActionPayloadMap>;

export const AssetUpdateContext = createContext({
  state: initialState,
  dispatch: (() => { }) as React.Dispatch<AssetUpdateAction>,
});

export const useAssetUpdate = (): [AssetUpdateStateType, React.Dispatch<AssetUpdateAction>] => {
  const { state, dispatch } = useContext(AssetUpdateContext);
  return [state, dispatch];
};

export const AssetUpdateProvider:FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [state, dispatch] = useReducer(AssetUpdateReducer, initialState);

  return (
    <AssetUpdateContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </AssetUpdateContext.Provider>
  );
};
